import CustomTransferList from 'Components/CustomTransferList'
import { WithLoader } from 'Components/WithLoader'
import { useGetProductGroupParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { useProducts } from 'Services/ProductManagement/Features/Product/Hooks'
import type { Product } from 'Services/ProductManagement/Features/Product/productSlice'
import { useUpdateSingleSalesRuleMutation } from 'Services/ProductManagement/Features/SalesRule/salesRuleApi'
import type { SalesRule } from 'Services/ProductManagement/Features/SalesRule/types'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  TextField
} from '@mui/material'
import { useMemo, useState } from 'react'
import { t } from 'ttag'

interface SalesRuleProductsEditProps {
  salesRule: SalesRule
  handleCancel: () => void
}

export function SalesRuleProductsEdit({
  salesRule,
  handleCancel
}: Readonly<SalesRuleProductsEditProps>) {
  const { allProducts, allProductsLoading, fetchAllProductsWithoutPagination } = useProducts()
  const { data: productGroupParameterGroup, isLoading: isProductGroupLoading } =
    useGetProductGroupParameterGroupQuery(undefined, { refetchOnMountOrArgChange: true })
  const [updateSalesRule, { isLoading: isUpdateLoading }] = useUpdateSingleSalesRuleMutation()

  const [searchText, setSearchText] = useState('')
  const [productGroupFilter, setProductGroupFilter] = useState('')

  const salesRulesProducts = allProducts.filter(product =>
    salesRule.product_codes.includes(product.product_code)
  )
  // Remove duplicate products from allSalesRuleProducts based on product_code
  const allSalesRulesProducts = Array.from(
    new Set(salesRulesProducts.map(product => product.product_code))
  ).map(product_code =>
    salesRulesProducts.find(product => product.product_code === product_code)
  ) as Product[]

  const productGroupOptions = productGroupParameterGroup?.parameters.map(
    ({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value,
      value: parameter_id
    })
  )
  // Add "All ticket types" option to the start of the array
  productGroupOptions?.unshift({ label: t`All ticket types`, value: '' })

  const filterOptions = (optionsToFilter: Product[]) => {
    const optionsFilteredBySearch = filterBySearch(optionsToFilter)
    const optionsFilteredByProductGroup = filterByProductGroup(optionsFilteredBySearch)
    return optionsFilteredByProductGroup
  }

  /**
   * Filters the given array of products based on the search text.
   *
   * The function checks if the search text is present in either the product name's finnish translation
   * or the product code. If the search text is not provided, it returns the original
   * array of products.
   */
  const filterBySearch = (optionsToFilter: Product[]) => {
    if (!searchText) return optionsToFilter
    return optionsToFilter.filter(product => {
      const productName = getTranslation(product.translations, 'fi').name.toLowerCase()
      const productCode = product.product_code.toLowerCase()
      const searchTextLower = searchText.toLowerCase()
      return productName.includes(searchTextLower) || productCode.includes(searchTextLower)
    })
  }

  const filterByProductGroup = (optionsToFilter: Product[]) => {
    if (!productGroupFilter) return optionsToFilter
    return optionsToFilter.filter(product => product.product_group === productGroupFilter)
  }

  /**
   * Filters out the products that are already selected for the sales rule.
   *
   * @returns Product[] - Array of products that are without sales rule.
   */
  const productsWithoutSalesRule = useMemo(() => {
    return allProducts.filter(
      product =>
        !allSalesRulesProducts.some(
          salesRuleProduct => salesRuleProduct.product_code === product.product_code
        )
    )
  }, [allProducts, allSalesRulesProducts])

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value
    setProductGroupFilter(newValue)
  }

  const handleSave = (selectedItems: Product[]) => {
    const productCodes = selectedItems.map(product => product.product_code)

    updateSalesRule({ id: salesRule.id, product_codes: productCodes })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Successfully saved changes.`)
        fetchAllProductsWithoutPagination()
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while saving changes.`)
      })
  }

  const renderItemContent = (product: Product) => {
    const labelId = `transfer-list-item-${product.id}-label`
    return (
      <ListItemText
        id={labelId}
        primary={`${getTranslation(product?.translations, 'fi').name}`}
        secondary={product?.product_code}
      />
    )
  }

  const isLoading = allProductsLoading === 'pending' || isProductGroupLoading || isUpdateLoading

  return (
    <WithLoader loading={isLoading}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <FormControl sx={{ width: '48%' }}>
          <TextField
            size="small"
            sx={{ background: 'white' }}
            label={t`Product search`}
            placeholder={t`Search products`}
            value={searchText}
            onChange={handleSearchTextChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </FormControl>
        <FormControl sx={{ width: '48%' }}>
          <InputLabel shrink id="product-group-select-label">{t`Product group`}</InputLabel>
          <Select
            name="product_group"
            labelId="product-group-select-label"
            value={productGroupFilter}
            size="small"
            displayEmpty
            label={t`Product group`}
            placeholder={t`Select product group`}
            onChange={handleChange}
            sx={{ background: 'white' }}
            input={<OutlinedInput notched label={t`Product group`} />}
          >
            {productGroupOptions?.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <CustomTransferList<Product>
        leftItemsHeaderText={t`Product listing`}
        rightItemsHeaderText={t`Sales rule's products`}
        leftItems={productsWithoutSalesRule}
        rightItems={allSalesRulesProducts}
        renderItemContent={renderItemContent}
        handleSave={handleSave}
        handleCancel={handleCancel}
        allowTransferBack={true}
        compareFn={(item: Product) => item.id}
        filterFn={filterOptions}
      />
    </WithLoader>
  )
}
